import AnimatedTier from '@atoms/AnimatedTier/AnimatedTier';
import Button from '@atoms/Button/Button';
import Cta from '@atoms/Cta/Cta';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Search from '@atoms/Search/Search';
import Text from '@atoms/Text/Text';
import { Tiers } from '@models/AnimatedTier';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';

type DashboardBannerContentCard = {
  readonly title: string;
  readonly text: string;
  readonly ctas?: Array<ILink>;
};

export interface IDashboardBanner {
  ariaLabels?: {
    'accessibility.search': string;
  };
  cards: Array<DashboardBannerContentCard>;
  cardGroups?: Array<{
    title: string;
    cards: Array<DashboardBannerContentCard>;
  }>;
  companyLocation?: string;
  companyLogo?: IImage;
  companyName?: string;
  dashboardCTA?: ILink;
  isLoyalty?: boolean;
  resultsPageUrl: string;
  searchCard?: {
    title: string;
    text: string;
    inputPlaceholder: string;
    cta: ILink;
  };
  searchEndpoint: string;
  subtext?: string;
  tiers?: Tiers;
  tierLink?: ILink;
  title?: string;
}

const DashboardCard = ({
  card,
  isMainCard,
}: {
  card: DashboardBannerContentCard;
  isMainCard?: boolean;
}) => (
  <a
    href={card.ctas?.[0]?.url}
    target={card.ctas?.[0]?.target}
    className={classNames(
      'tw-flex tw-h-full tw-grow tw-flex-col tw-items-start tw-bg-grey-10 tw-px-xs tw-py-sm',
      'tw-group tw-justify-between tw-transition-colors  md:tw-px-sm',
      card.ctas?.length && 'hover:tw-bg-grey-20',
      isMainCard
        ? 'max-md:tw-gap-4 md:tw-min-h-[200px]'
        : 'max-md:tw-gap-6 md:tw-min-h-[136px]',
    )}
  >
    <div className={'tw-flex tw-w-full'}>
      <Text
        as="p"
        variant={isMainCard ? 'ds-headline-2' : 'heading-24-light'}
        className={classNames(
          'tw-mr-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap  tw-font-light tw-text-black ',
        )}
      >
        {card.title}
      </Text>
      {!!card.ctas?.length && (
        <Icon
          name="arrow-right"
          className="tw-my-auto tw-ml-auto tw-max-w-5 tw-shrink-0 tw-text-grey-40 tw-transition-colors group-hover:tw-text-grey-100 group-focus:tw-text-grey-100 @md/article-card:tw-max-w-6"
          size={24}
        />
      )}
    </div>
    <Text
      variant="body"
      className="tw-text-grey-80"
      dangerouslySetInnerHTML={{ __html: card.text }}
    />
  </a>
);

const DashboardBanner = ({
  ariaLabels,
  cardGroups,
  cards,
  companyLocation,
  companyLogo,
  companyName,
  dashboardCTA,
  isLoyalty,
  resultsPageUrl,
  searchCard,
  searchEndpoint,
  subtext,
  tierLink,
  tiers,
  title,
}: IDashboardBanner) => {
  const { register, handleSubmit, setValue } = useForm({ defaultValues: {} });

  const onSubmit = (payload: { freetext?: string }) => {
    if (!payload.freetext) return;

    window.location.href = `${resultsPageUrl}?query=${payload.freetext}`;
  };

  return (
    <section
      className={classNames(
        'tw-container tw-my-20 tw-justify-center md:tw-my-[120px]',
        isLoyalty ? 'tw-my-[90px]' : 'tw-my-20',
      )}
    >
      {(title || subtext) && (
        <div className="tw-mb-14 tw-grid tw-gap-8 tw-text-center md:tw-mb-20 md:tw-gap-10">
          {title && (
            <h1
              className="tw-font-serif tw-text-4xl tw-text-grey-100 md:tw-text-7xl xl:tw-text-8xl"
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
          {subtext && (
            <Text variant="body" className="tw-text-grey-80">
              {subtext}
            </Text>
          )}
        </div>
      )}
      {searchCard ? (
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-gap-8 lg:tw-flex-row ">
          <div
            className={classNames(
              'tw-relative',
              cards.length > 1 ? 'tw-basis-1/3' : 'tw-basis-1/2',
            )}
          >
            <div className="tw-dark tw-top-0 tw-flex tw-h-full tw-flex-col tw-justify-between tw-bg-blue tw-px-4 tw-py-8 tw-text-white lg:tw-px-6 lg:tw-py-10">
              <div>
                <Text variant="heading-28" className="tw-mb-2 lg:tw-hidden">
                  {searchCard.title}
                </Text>
                <Text variant="heading-36" className="tw-mb-6 max-lg:tw-hidden">
                  {searchCard.title}
                </Text>
                <Text variant="body">{searchCard.text}</Text>
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="tw-mt-10 tw-flex tw-flex-col tw-gap-6"
              >
                <Search
                  autoCompleteEndpoint={`${searchEndpoint}?pageurl=${resultsPageUrl}&freetext`}
                  setValue={setValue}
                  label={ariaLabels?.['accessibility.search']}
                  name="freetext"
                  register={register}
                  placeholder={searchCard.inputPlaceholder}
                  backgroundColor="tw-bg-transparent"
                  darkMode
                  onAutoCompleteSelect={(value) =>
                    onSubmit({ freetext: value })
                  }
                />
                <Cta {...searchCard.cta} />
              </form>
            </div>
          </div>
          <div
            className={classNames(
              'tw-grid tw-flex-grow tw-basis-1/2 tw-grid-cols-1 tw-gap-4 md:tw-gap-8',
              cards.length > 1 && 'md:tw-grid-cols-2',
            )}
          >
            {cards.map((card) => (
              <div key={card.title}>
                <DashboardCard card={card} isMainCard />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            'tw-grid tw-grid-cols-1 tw-gap-4 md:tw-gap-8 ',
            cards.length > 2 && !isLoyalty && 'lg:tw-grid-cols-3',
            cards.length >= 4 && !isLoyalty && 'xl:tw-grid-cols-4',
            isLoyalty
              ? 'md:tw-grid-cols-1 lg:tw-grid-cols-2'
              : 'md:tw-grid-cols-2',
          )}
        >
          {isLoyalty && (
            <div className="tw-dark tw-flex tw-flex-col tw-self-start tw-bg-blue tw-px-4 tw-py-8 tw-text-white xl:tw-px-6 xl:tw-py-10">
              <div className="tw-mb-[60px] tw-flex tw-gap-x-2 xl:tw-mb-10 xl:tw-gap-x-6">
                {companyLogo && (
                  <div className="tw-h-12 tw-w-12 xl:tw-h-[80px] xl:tw-w-[80px]">
                    <Image
                      imageClasses="tw-rounded-[48px] xl:tw-rounded-[80px] tw-w-[48px] tw-h-[48px] xl:tw-w-[80px] xl:tw-h-[80px]"
                      image={companyLogo}
                    />
                  </div>
                )}
                <div className="tw-flex tw-flex-col">
                  {companyName && (
                    <Text variant="headline" as="p" className="tw-mb-1">
                      {companyName}
                    </Text>
                  )}
                  {companyLocation && (
                    <Text as="p" variant="label-12">
                      {companyLocation}
                    </Text>
                  )}
                </div>
              </div>

              {!!tiers?.length && (
                <div className="tw-mb-[60px] xl:tw-mb-10">
                  <AnimatedTier tiers={tiers} />
                </div>
              )}

              <div className="md:tw-flex md:tw-flex-row-reverse md:tw-items-center md:tw-justify-between">
                {dashboardCTA && (
                  <Button
                    as="link"
                    buttonClasses="tw-w-full md:tw-w-auto"
                    href={dashboardCTA.url}
                    label={dashboardCTA.text}
                    target={dashboardCTA.target}
                  />
                )}
                {tierLink?.text && tierLink.url && (
                  <div className="tw-mt-6 md:tw-mt-0">
                    <Cta
                      url={tierLink.url}
                      text={tierLink.text}
                      target={tierLink.target}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {isLoyalty ? (
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 md:tw-gap-8">
              {cards.map((card) => (
                <div key={card.title}>
                  <DashboardCard card={card} isMainCard />
                </div>
              ))}
            </div>
          ) : (
            cards.map((card) => (
              <div key={card.title}>
                <DashboardCard card={card} isMainCard />
              </div>
            ))
          )}
        </div>
      )}
      {!!cardGroups?.length &&
        cardGroups.map((group) => (
          <div key={group.title} className="tw-mt-12 md:tw-mt-20">
            <Text
              variant="body-small"
              className="tw-mb-4 tw-uppercase tw-text-black"
            >
              {group.title}
            </Text>
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 md:tw-gap-8 xl:tw-grid-cols-4">
              {group.cards.map((card) => (
                <div key={card.title}>
                  <DashboardCard card={card} />
                </div>
              ))}
            </div>
          </div>
        ))}
    </section>
  );
};

export default DashboardBanner;
